import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { searchService } from '@services/searchService';
import type { SearchResultItem } from '@services/searchService';
import { searchHistory } from '@lib/searchHistory';
import { getPeopleOnLine, subscribeToPeopleOnLineChanges } from '@lib/peopleOnlineStatus';

import './SearchAutocomplete.css';

interface SearchAutocompleteProps {
  searchText: string;
  anchorRect: DOMRect;
  onClose: () => void;
  onSelectProject: (item: SearchResultItem) => void;
  onSelectDashboard: (item: SearchResultItem) => void;
  onSelectPerson: (item: SearchResultItem) => void;
  onSelectItem: (item: SearchResultItem) => void;
}

const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  searchText,
  anchorRect,
  onClose,
  onSelectProject,
  onSelectDashboard,
  onSelectPerson,
  onSelectItem,
}) =>
{
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryMode, setIsHistoryMode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadHistory = useCallback(() =>
  {
    const history = searchHistory.getSearchHistory();
    setResults(history);
    setIsHistoryMode(true);
    setIsLoading(false);
  }, []);

  const fetchResults = useCallback(
    (text: string) =>
    {
      if (text.length < 1)
      {
        loadHistory();
        return;
      }
      setIsHistoryMode(false);
      setIsLoading(true);
      searchService.search(text).then(data =>
      {
        setResults(data);
        setIsLoading(false);
      });
    },
    [loadHistory],
  );

  useEffect(() =>
  {
    if (debounceRef.current)
    {
      clearTimeout(debounceRef.current);
    }

    if (searchText.length < 1)
    {
      loadHistory();
      return;
    }

    debounceRef.current = setTimeout(() =>
    {
      fetchResults(searchText);
    }, 600);

    return () =>
    {
      if (debounceRef.current)
      {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchText, fetchResults, loadHistory]);

  useEffect(() =>
  {
    // Presence can arrive (socket:client:allConnectedUsersWeb / listUserOnLine) after
    // this fetch already resolved - re-apply it to the results already on screen.
    return subscribeToPeopleOnLineChanges(() =>
    {
      setResults(prevResults => getPeopleOnLine(prevResults));
    });
  }, []);

  const handleItemClick = (item: SearchResultItem) =>
  {
    if (item.TypeId === 0) return;

    // Save to history based on type
    if (item.TypeId === 9)
    {
      searchHistory.addProjectToHistory(item);
      onSelectProject(item);
    }
    else if (item.IsDashboardsFilter)
    {
      searchHistory.addDashboardToHistory(item);
      onSelectDashboard(item);
    }
    else if (item.TypeId === 15)
    {
      searchHistory.addPersonToHistory(item);
      onSelectPerson(item);
    }
    else
    {
      onSelectItem(item);
    }
    onClose();
  };

  const getImageUrl = (item: SearchResultItem): string | null =>
  {
    if (item.TypeId === 15 && item.ImageName)
    {
      // If already a full URL, use as-is
      if (item.ImageName.startsWith('http'))
      {
        return item.ImageName;
      }
      // Build URL using files CDN
      return `https://files.corgee.com/2/ProfileImages/${item.ImageName}`;
    }
    return null;
  };

  const renderIcon = (item: SearchResultItem) =>
  {
    const imgUrl = getImageUrl(item);
    if (imgUrl)
    {
      return (
        <div className="search-autocomplete-item-icon">
          <img
            src={imgUrl}
            alt={item.Name}
            onError={e =>
            {
              // Hide broken image, show fallback icon
              const target = e.currentTarget;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent)
              {
                const icon = document.createElement('i');
                icon.className = 'fa fa-user';
                parent.appendChild(icon);
              }
            }}
          />
        </div>
      );
    }
    // Icon based on item type
    let iconClass = 'fa fa-thumb-tack';
    if (item.TypeId === 9)
    {
      iconClass = 'icons-menu-assignment';
    }
    else if (item.IsDashboardsFilter)
    {
      iconClass = 'icons-menu-newsfeed';
    }
    return (
      <div className="search-autocomplete-item-icon">
        <i className={iconClass}></i>
      </div>
    );
  };

  const renderActions = (item: SearchResultItem) =>
  {
    if (item.TypeId === 9)
    {
      // Projects: only folder icon
      return (
        <div className="search-autocomplete-item-actions">
          <span
            className="action-icon"
            title="Open"
            onClick={e =>
            {
              e.stopPropagation();
              handleItemClick(item);
            }}
          >
            <i className="fa fa-folder-open-o"></i>
          </span>
        </div>
      );
    }
    if (item.IsDashboardsFilter || item.TypeId === 15)
    {
      // Dashboards and People: only external link icon
      return (
        <div className="search-autocomplete-item-actions">
          <span
            className="action-icon"
            title="Open"
            onClick={e =>
            {
              e.stopPropagation();
              handleItemClick(item);
            }}
          >
            <i className="fa fa-external-link"></i>
          </span>
        </div>
      );
    }
    // Default: both icons
    return (
      <div className="search-autocomplete-item-actions">
        <span
          className="action-icon"
          title="Open"
          onClick={e =>
          {
            e.stopPropagation();
            handleItemClick(item);
          }}
        >
          <i className="fa fa-folder-open-o"></i>
        </span>
        <span
          className="action-icon"
          title="Open in new tab"
          onClick={e =>
          {
            e.stopPropagation();
            handleItemClick(item);
          }}
        >
          <i className="fa fa-external-link"></i>
        </span>
      </div>
    );
  };

  const renderContent = () =>
  {
    if (isLoading)
    {
      return <div className="search-autocomplete-loading">Searching '{searchText}' ...</div>;
    }

    if (results.length === 0)
    {
      if (isHistoryMode)
      {
        return <div className="search-autocomplete-empty">No recent items</div>;
      }
      return (
        <div className="search-autocomplete-empty">
          No results matching '{searchText}', press Enter to find in all information.
        </div>
      );
    }

    return results.map((item, index) =>
    {
      if (item.TypeId === 0)
      {
        return (
          <div key={`header-${index}`} className="search-autocomplete-header">
            {item.Name}
          </div>
        );
      }
      return (
        <div
          key={`item-${item.TypeId}-${item.ID}-${index}`}
          className="search-autocomplete-item"
          onClick={() => handleItemClick(item)}
        >
          {renderIcon(item)}
          <span className="search-autocomplete-item-name">{item.Name}</span>
          {item.TypeId === 15 && (
            <span
              className={
                item.isOnLine ? 'search-autocomplete-online-dot' : 'search-autocomplete-offline-dot'
              }
            />
          )}
          {item.IsDashboardsFilter && (
            <span className="search-autocomplete-item-project">
              {item.ProjectName || 'DASHBOARDS'}
            </span>
          )}
          {renderActions(item)}
        </div>
      );
    });
  };

  const style: React.CSSProperties = {
    top: anchorRect.bottom + 2,
    left: anchorRect.left,
    minWidth: Math.max(anchorRect.width, 450),
  };

  return ReactDOM.createPortal(
    <>
      <div className="search-autocomplete-overlay" onClick={onClose} />
      <div ref={panelRef} className="search-autocomplete" style={style}>
        {renderContent()}
      </div>
    </>,
    document.body,
  );
};

export default SearchAutocomplete;
